'use client';

import { Feature } from '@/components/common/feature-variants';
import { Button } from '@heroui/button';
import { Card, CardBody } from '@heroui/card';
import { Link } from '@heroui/link';
import { ArrowRight, Check, CheckCircle, Users, Zap } from 'lucide-react';

export default function AboutPage() {
  return (
    <>
      <section className="section-md bg-gradient-to-b from-primary/20 to-background/5">
        <div className="container container-max">
          {/* Hero Section */}
          <div className="space-y-4 text-center">
            <h1 className="mb-6 font-extrabold text-transparent hero-title bg-clip-text bg-gradient-to-l from-primary to-secondary ">
              About PayStop
            </h1>

            <p className="max-w-2xl mx-auto text-lg text-foreground/70">
              Empowering Local Businesses with Seamless Financial Services
            </p>
          </div>
        </div>
      </section>

      <section className="section-md">
        <div className="container space-y-16 container-max">
          {/* Mission Statement */}
          <div className="max-w-3xl mx-auto text-center">
            <p className="leading-relaxed text-foreground/70 ">
              At PayStop, we believe that financial services should be accessible to everyone,
              especially local businesses and their communities. Our platform is dedicated to
              empowering entrepreneurs and individuals by providing secure, efficient, and
              user-friendly digital banking solutions. By offering a simple way to manage financial
              transactions, PayStop helps local businesses enhance their service offerings and
              expand their customer base.
            </p>
          </div>

          {/* Vision */}
          <div className="max-w-3xl mx-auto space-y-4 text-center">
            <h2 className="title-md">Our Vision</h2>
            <p className="leading-relaxed text-foreground/70 ">
              To support and empower local businesses by providing easy access to financial services
              that help foster growth, increase convenience, and drive economic progress in
              communities.
            </p>
          </div>
        </div>
      </section>
      <section className="section-md bg-primary-50 dark:bg-background">
        <div className="container space-y-20 container-md">
          {/* What We Do */}
          <div className="space-y-8">
            <h2 className="font-bold text-center title">What We Do</h2>
            <p className="max-w-3xl mx-auto leading-relaxed text-center text-foreground/70">
              PayStop enables local businesses to offer a wide range of financial services to their
              customers, all through a mobile app. Our platform provides businesses and individuals
              with the ability to withdraw or deposit money into digital banking accounts, mobile
              wallets, and traditional bank accounts securely and efficiently. Whether you&apos;re a
              small shop, kiosk, or other local business, PayStop helps you provide essential
              financial services to your customers.
            </p>{' '}
          </div>
          <div className="grid grid-cols-1 gap-12 md:grid-cols-3">
            {[
              {
                title: 'Simple Deposits and Withdrawals',
                description:
                  'Allow your customers to easily withdraw or deposit money into their digital banking accounts, mobile wallets, or traditional bank accounts, right at your location.',
                icon: <CheckCircle className=" text-primary" />,
              },
              {
                title: 'Money Transfers',
                description:
                  'Enable your customers to send money to family, friends, or other businesses.',
                icon: <Users className=" text-primary" />,
              },
              {
                title: 'Mobile Wallet Integration',
                description:
                  'We integrate with various mobile wallet services and financial institutions to ensure your customers have the flexibility to choose their preferred payment methods.',
                icon: <Zap className=" text-primary" />,
              },
            ].map((feature, index) => (
              <Feature
                key={index}
                description={feature.description}
                title={feature.title}
                variant="horizontal"
                icon={feature.icon}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="section-md ">
        <div className="container container-max space-y-7">
          {/* How It Works */}
          <div className="space-y-8 ">
            <h2 className="title">How It Works</h2>
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              {[
                {
                  title: 'Become an Agent',
                  description: 'Register on PayStop and invite sub-agents.',
                },
                {
                  title: 'Roles of Agents',
                  description: 'Our system supports Super Agents, Agent Admins, and Sub-Agents.',
                },
                {
                  title: 'Real-Time Transactions',
                  description: 'Secure, real-time deposits, withdrawals, and transfers.',
                },
              ].map((step, index) => (
                <Feature
                  key={index}
                  description={step.description}
                  title={step.title}
                  showIcon={false}
                  variant="numbered"
                  icon={''}
                  index={index}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
      <section className=" section-md bg-slate-50 dark:bg-background">
        <div className="container container-max space-y-7">
          <div className="grid items-center gap-20 md:grid-cols-2">
            {/* Our Values */}
            <div className="space-y-8">
              <h2 className="title">Our Values</h2>
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                {[
                  {
                    title: 'Innovation',
                    description: 'Constantly evolving our platform with new technologies.',
                  },
                  {
                    title: 'Security',
                    description: 'Advanced encryption and multi-factor authentication.',
                  },
                  {
                    title: 'Customer-Centric',
                    description: 'Reliable, transparent, and user-friendly services.',
                  },
                  {
                    title: 'Community Empowerment',
                    description: 'Supporting businesses to drive economic growth.',
                  },
                ].map((value, index) => (
                  <Feature
                    key={index}
                    description={value.description}
                    title={value.title}
                    variant="bordered"
                    showIcon={false}
                    className="bg-content1"
                  />
                ))}
              </div>
            </div>

            {/* Our Team */}
            <div className="space-y-8">
              <h2 className="title">Our Team</h2>
              <p className="max-w-3xl mx-auto leading-relaxed text-foreground/70 ">
                PayStop is powered by a dedicated team of professionals committed to delivering the
                best financial services to local businesses. Our team includes financial experts,
                tech innovators, and customer service professionals who all share a common goal: to
                make financial transactions easier and more accessible for local businesses and
                their customers.
              </p>
            </div>
          </div>
        </div>
      </section>
      <section className="section-md">
        <div className="container text-center container-max">
          {/* Why Choose Us */}
          <div className="space-y-12">
            <h2 className="title">
              Why Choose <span className="text-primary">PayStop?</span>
            </h2>
            <div className="grid grid-cols-1 gap-8 text-left md:grid-cols-3">
              {[
                {
                  icon: Check,
                  title: 'Convenience',
                  description: 'Easy access to financial services through local agents.',
                },
                {
                  icon: Check,
                  title: 'Flexibility',
                  description: 'Supports multiple transaction types.',
                },
                {
                  icon: Check,
                  title: 'Affordable',
                  description: 'Competitive transaction fees for businesses and customers.',
                },
              ].map((reason, index) => (
                <Feature
                  key={index}
                  description={reason.description}
                  title={reason.title}
                  variant="bordered"
                  icon={<reason.icon className="text-primary" />}
                  className="box-border rounded-large shadow-medium border-divider bg-content1"
                />
              ))}
            </div>
          </div>
        </div>
      </section>
      <section className="section-md bg-primary-50 dark:bg-background">
        <div className="container space-y-10 container-max">
          {/* Call to Action */}
          <div className="space-y-8 text-center rounded-3xl">
            <h2 className="title">Join Us Today</h2>
            <p className="max-w-3xl mx-auto leading-relaxed text-foreground/70">
              PayStop is more than just a financial service platform – it's a tool for local
              businesses to enhance their offerings, increase revenue, and better serve their
              communities. Whether you're a small business owner or an entrepreneur, we invite you
              to join us and become part of the PayStop network.
            </p>
            <Link href="/become-agent">
              <Button color="primary" size="lg">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
